import type { SearchParamsSitum } from "@/features/app/types";

export interface URLProps {
  buildingID?: number | null;
  floorID?: number | null;
  poiID?: number | null;
  categoryId?: number | null;
  navigationFrom?: number | null;
  navigationTo?: number | null;
  lockCameraToBuilding?: boolean | null;
  whiteLabel?: boolean | null;
  lang?: string | null;
  externalCategoryId?: string | null;
  externalPoiId?: string | null;
  externalNavigationFrom?: string | null;
  externalNavigationTo?: string | null;
  flightId?: string | null;
  flightNavigationFrom?: string | null;
  flightNavigationTo?: string | null;
  shared_session_id?: string | null;
}

/**
 * Map of every deprecated search param to its canonical snake_case replacement.
 * Order matters: entries are processed in order, so more specific keys should
 * come before more general ones if there is any ambiguity.
 */
const DEPRECATED_PARAM_MIGRATIONS: ReadonlyArray<{
  deprecated: SearchParamsSitum;
  canonical: SearchParamsSitum;
}> = [
  { canonical: "building_id", deprecated: "buildingid" },
  { canonical: "floor_id", deprecated: "floorid" },
  { canonical: "poi_id", deprecated: "poiid" },
  { canonical: "wait_for_auth", deprecated: "waitforauth" },
  { canonical: "device_id", deprecated: "deviceId" },
  { canonical: "lock_camera_to_building", deprecated: "lockCameraToBuilding" },
];

/**
 * Replaces any deprecated (non-snake_case) URL search params with their
 * canonical snake_case equivalents using `history.replaceState` so the
 * migration is transparent to the user and does not add a history entry.
 *
 * Call this once as early as possible during app initialisation, before
 * any other code reads `window.location.search`.
 */
export function migrateDeprecatedParams(): void {
  const urlParams = new URLSearchParams(window.location.search);
  let changed = false;

  for (const { deprecated, canonical } of DEPRECATED_PARAM_MIGRATIONS) {
    if (!urlParams.has(deprecated)) continue;

    // Canonical param wins if both happen to be present; otherwise promote the deprecated value.
    if (!urlParams.has(canonical)) {
      urlParams.set(canonical, urlParams.get(deprecated) as string);
    }
    urlParams.delete(deprecated);
    changed = true;
  }

  if (changed) {
    window.history.replaceState(
      null,
      document.title,
      `${location.pathname}?${urlParams}`,
    );
  }
}

const updateParamOrRemove = (
  urlParams: URLSearchParams,
  paramName: string,
  value: string | number | null,
) => {
  if (value === null) {
    urlParams.delete(paramName);
  } else if (value != undefined) {
    urlParams.set(paramName, value.toString());
  }
};

export const updateUrl: (props: URLProps) => void = ({
  buildingID,
  floorID,
  poiID,
  categoryId,
  externalCategoryId,
  externalPoiId,
  externalNavigationFrom,
  externalNavigationTo,
  flightId,
  flightNavigationFrom,
  flightNavigationTo,
  navigationFrom,
  navigationTo,
  lockCameraToBuilding,
  whiteLabel,
  lang,
  shared_session_id,
}) => {
  const urlParams = new URLSearchParams(window.location.search);

  // Always remove deprecated param variants to keep the URL clean.
  for (const { deprecated } of DEPRECATED_PARAM_MIGRATIONS) {
    urlParams.delete(deprecated);
  }

  updateParamOrRemove(
    urlParams,
    "building_id" satisfies SearchParamsSitum,
    buildingID,
  );
  updateParamOrRemove(
    urlParams,
    "floor_id" satisfies SearchParamsSitum,
    floorID,
  );
  updateParamOrRemove(urlParams, "poi_id" satisfies SearchParamsSitum, poiID);
  updateParamOrRemove(urlParams, "external_poi_id", externalPoiId);
  updateParamOrRemove(urlParams, "category_id", categoryId);
  updateParamOrRemove(urlParams, "external_category_id", externalCategoryId);
  updateParamOrRemove(urlParams, "navigation_from", navigationFrom);
  updateParamOrRemove(
    urlParams,
    "external_navigation_from",
    externalNavigationFrom,
  );
  updateParamOrRemove(urlParams, "navigation_to", navigationTo);
  updateParamOrRemove(
    urlParams,
    "external_navigation_to",
    externalNavigationTo,
  );
  updateParamOrRemove(urlParams, "flight_id", flightId);
  updateParamOrRemove(
    urlParams,
    "flight_navigation_from",
    flightNavigationFrom,
  );
  updateParamOrRemove(urlParams, "flight_navigation_to", flightNavigationTo);
  updateParamOrRemove(urlParams, "shared_session_id", shared_session_id);
  updateParamOrRemove(urlParams, "lng", lang);

  if (lockCameraToBuilding == null || lockCameraToBuilding == true) {
    urlParams.delete("lock_camera_to_building" satisfies SearchParamsSitum);
  } else {
    urlParams.set(
      "lock_camera_to_building" satisfies SearchParamsSitum,
      String(lockCameraToBuilding),
    );
  }

  if (whiteLabel === null) {
    urlParams.delete("wl");
  } else if (whiteLabel) {
    urlParams.set("wl", String(whiteLabel));
  }

  window.history.pushState(
    "data",
    document.title,
    location.pathname + "?" + urlParams,
  );
};

const joinPaths = (basePath: string, path: string) => {
  if (!basePath || basePath === "/") return `/${path.replace(/^\//, "")}`;
  return `${basePath.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

export const replaceBaseURL = ({
  url,
  base,
}: {
  url: string;
  base: string;
}) => {
  const originalUrlObj = new URL(url);
  const newUrlObj = new URL(base);

  originalUrlObj.protocol = newUrlObj.protocol;
  originalUrlObj.port = newUrlObj.port;
  originalUrlObj.hostname = newUrlObj.hostname;
  originalUrlObj.pathname = joinPaths(
    newUrlObj.pathname,
    originalUrlObj.pathname,
  );

  return originalUrlObj;
};

/**
 * Returns true if the given URL has a profile name in its pathname
 */
export function urlHasProfile(url: string): boolean {
  const { pathname } = new URL(url);
  return pathname.split("/").filter(Boolean).length > 0;
}

/**
 * Removes `domain` and `apikey` query params from a URL when a profile is
 * present in the pathname. If no profile is detected the URL is returned
 * unchanged.
 */
export function stripAuthParamsIfProfile(url: string): string {
  if (!urlHasProfile(url)) return url;

  const urlObj = new URL(url);
  urlObj.searchParams.delete("domain");
  urlObj.searchParams.delete("apikey");
  return urlObj.toString();
}

export function removeProfileFromUrlWithReload() {
  const currentUrl = new URL(window.location.href);
  const pathSegments = currentUrl.pathname.split("/").filter(Boolean);

  if (pathSegments.length > 0) {
    currentUrl.pathname = "/";
    window.location.href = currentUrl.toString();
  }
}

/**
 * Parses a URL search parameter and returns it as a string or number.
 *
 * This function supports two overloads:
 * - When `type` is `"number"`, it parses the parameter value as an integer and returns `number | null`.
 * - When `type` is `"string"` (or omitted, defaulting to `"string"`), it returns the raw parameter value as `string | null`.
 *
 * @param searchParams - The URLSearchParams object to extract the parameter from.
 * @param paramName - The name of the search parameter to parse (must be a valid SearchParamsSitum value).
 * @param type - The type to parse the parameter as. Defaults to `"string"`.
 * @returns The parsed parameter value, or `null` if the parameter is missing or invalid.
 *
 * @example
 * ```ts
 * const params = new URLSearchParams("?building_id=123&lang=en");
 * parseParam(params, "building_id", "number"); // Returns: 123
 * parseParam(params, "lang"); // Returns: "en"
 * parseParam(params, "missing"); // Returns: null
 * ```
 */
export function parseQueryParam(
  searchParams: URLSearchParams,
  paramName: SearchParamsSitum,
  type: "number",
): number | null;
export function parseQueryParam(
  searchParams: URLSearchParams,
  paramName: SearchParamsSitum,
  type?: "string",
): string | null;
export function parseQueryParam(
  searchParams: URLSearchParams,
  paramName: SearchParamsSitum,
  type: "number" | "string" = "string",
): number | string | null {
  const value = searchParams.get(paramName);
  if (type === "string") return value;

  if (value == null) return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}
